import type { IKeyObjective } from 'legacy/lut/classes/models/partner360/IFormOptionsJBP'
import type { IKIBusinessPriority } from 'legacy/lut/features/partner360/jbpSettings/models/kiBusinessPriority'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import type { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { getCockpitInitiativeHierarchyQuality, getCockpitInitiativeRows } from '../../../../lib'
import {
  selectCockpitCategory,
  selectCockpitGeoMappingItems,
  selectCockpitOffering,
  selectEffectiveCockpitActualsSync,
  selectVisibleCockpitFinSobRows,
  selectVisibleCockpitPartnerData,
} from '../../../../model/selectors'
import type {
  CockpitPeriodKey,
  ICockpitKeyInitiative,
  ICockpitPlanningPeriod,
} from '../../../../model/types'
import type { TCockpitExportSlot } from '../../../components/cockpitExport'
import {
  CockpitInitiativesHierarchy,
  type TCockpitInitiativeHierarchyValueColumnsFactory,
} from '../cockpitInitiativesHierarchy'

import { getCockpitInitiativesQualityHierarchyColumns } from './helpers/getCockpitInitiativesQualityHierarchyColumns'

interface ICockpitInitiativesQualityHierarchyProps {
  initiatives: ICockpitKeyInitiative[]
  businessPriorities: IKIBusinessPriority[]
  selectedMetric: ActualsValueTypes
  isLocalCurrency: boolean
  selectedPeriods: CockpitPeriodKey[]
  planningPeriod: ICockpitPlanningPeriod | null
  currency: string
  keyObjectives: IKeyObjective[]
  exportSlot?: TCockpitExportSlot
}

/** Multi-partner quality view sharing the Initiatives table's complete Geo/Product hierarchy. */
export const CockpitInitiativesQualityHierarchy = ({
  initiatives,
  businessPriorities,
  selectedMetric,
  isLocalCurrency,
  selectedPeriods,
  planningPeriod,
  currency,
  keyObjectives,
  exportSlot,
}: ICockpitInitiativesQualityHierarchyProps) => {
  const finSobRows = useSelector(selectVisibleCockpitFinSobRows)
  const partners = useSelector(selectVisibleCockpitPartnerData)
  const geoMappingItems = useSelector(selectCockpitGeoMappingItems)
  const offering = useSelector(selectCockpitOffering)
  const selectedCategory = useSelector(selectCockpitCategory)
  const { fYear, currentFQuarter, fQuarterActualsPercentage } = useSelector(
    selectEffectiveCockpitActualsSync
  )

  const rows = useMemo(
    () =>
      getCockpitInitiativeRows({
        initiatives,
        kiBusinessPriorities: businessPriorities,
        selectedMetric,
        isLocalCurrency,
        selectedPeriods,
        planningPeriod,
        keyObjectives,
      }),
    [
      initiatives,
      businessPriorities,
      selectedMetric,
      isLocalCurrency,
      selectedPeriods,
      planningPeriod,
      keyObjectives,
    ]
  )

  const valueColumnsFactory = useCallback<TCockpitInitiativeHierarchyValueColumnsFactory>(
    ({ isExpandable }) => {
      const getQuality = getCockpitInitiativeHierarchyQuality({
        initiatives,
        finSobRows,
        partners,
        geoMappingItems,
        businessPriorities,
        keyObjectives,
        offering,
        fYear,
        currentFQuarter,
        fQuarterActualsPercentage,
        selectedMetric,
        selectedCurrency: currency,
        selectedPeriods,
        planningPeriod,
        isLocalCurrency,
        selectedCategory,
      })

      return getCockpitInitiativesQualityHierarchyColumns({
        getQuality,
        currency,
        isExpandable,
      })
    },
    [
      initiatives,
      finSobRows,
      partners,
      geoMappingItems,
      businessPriorities,
      keyObjectives,
      offering,
      fYear,
      currentFQuarter,
      fQuarterActualsPercentage,
      selectedMetric,
      currency,
      selectedPeriods,
      planningPeriod,
      isLocalCurrency,
      selectedCategory,
    ]
  )

  return (
    <CockpitInitiativesHierarchy
      rows={rows}
      currency={currency}
      showGeoHierarchy
      exportSlot={exportSlot}
      exportKey="initiativesQuality"
      valueColumnsFactory={valueColumnsFactory}
    />
  )
}

CockpitInitiativesQualityHierarchy.displayName = 'CockpitInitiativesQualityHierarchy'
